import { useId, type ReactNode } from 'react';
import { Controller, useWatch, type FieldPath, type UseFormReturn } from 'react-hook-form';
import { Baby, HeartHandshake, IdCard, Info, MapPin, Siren, UserRound, Wallet, type LucideIcon } from 'lucide-react';
import {
  BIRTH_WEIGHT_GRAMS,
  BLOOD_GROUP,
  FAMILY_RELATION,
  GESTATIONAL_AGE_EXTRA_DAYS_MAX,
  GESTATIONAL_AGE_WEEKS,
  GUARDIAN_RELATION,
  PATIENT_CATEGORY,
  SEX,
  nameFieldLabel,
  type CountryProfile,
  type FacilityKind,
  type FamilyRelation,
  type GuardianRelation,
  type NamePart,
  type RegisterPatientInput,
} from '@his/shared';
import { tr } from '@/lib/i18n-core';
import { languageOptions } from '@/lib/country';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';
import { categoryLabel } from './patient-labels';
import { Badge, Field, Input, Select, Textarea } from '@/components/ui/primitives';
import { ChoiceButtons, type ChoiceOption as Choice } from '@/components/ui/choice-buttons';
import { DobAgeField } from './DobAgeField';
import { PatientPhotoField } from './PatientPhotoField';
import type { PhotoDraft } from './patient-photo';
import { IdentityDocumentFields } from './IdentityDocumentFields';
import { PatientPicker, type PatientSummary } from './PatientPicker';
import { familyRelationLabel } from './PatientFamily';
import { PhoneInput } from './PhoneInput';
import { RegistrationSection } from './RegistrationSection';
import {
  birthWeightBand,
  gestationBand,
  identityRequired,
  identityShown,
  nameParts,
  type PatientAgeFacts,
  type RegistrationMode,
  type RegistrationSectionId,
} from './registration-rules';

type Form = UseFormReturn<RegisterPatientInput>;

export interface FamilyLinkControl {
  patient: PatientSummary | null;
  relation: FamilyRelation;
  onChange: (patient: PatientSummary | null, relation: FamilyRelation) => void;
}

export interface PhotoControl {
  value: PhotoDraft;
  onChange: (draft: PhotoDraft) => void;
  currentKey: string | null;
  name: string;
}

export interface PatientFormSectionsProps {
  form: Form;
  purpose: 'register' | 'edit';
  profile: CountryProfile;
  facility: FacilityKind;
  keepsFullRecords: boolean;
  mode: RegistrationMode;
  facts: PatientAgeFacts;
  progress: Partial<Record<RegistrationSectionId, boolean>>;
  autoFocusName?: boolean;
  onLookupField?: () => void;
  onIdentityCommit?: () => void;
  identityExtra?: ReactNode;
  family?: FamilyLinkControl;
  photo?: PhotoControl;
  extraSections?: ReactNode;
  insuranceInForm?: boolean;
}

const MARITAL_STATUS = ['single', 'married', 'widowed', 'divorced', 'unknown'] as const;

function sexLabel(sex: (typeof SEX)[number]): string {
  return tr('opd', sex === 'unknown' ? 'unknown' : sex);
}

function bloodGroupLabel(group: (typeof BLOOD_GROUP)[number]): string {
  return group === 'unknown' ? tr('opd', 'unknown') : group.replace('_pos', '+').replace('_neg', '−').toUpperCase();
}

function guardianRelationLabel(relation: GuardianRelation): string {
  if (relation === 'self') return tr('opd', 'guardian_relation_self');
  if (relation === 'other') return tr('opd', 'guardian_relation_other');
  return familyRelationLabel(relation);
}

function nameLabel(profile: CountryProfile, part: NamePart): string {
  return part === 'first' ? tr('opd', 'first_name') : nameFieldLabel(profile, part);
}

const NAME_FIELD: Record<NamePart, 'firstName' | 'middleName' | 'fatherOrHusbandName' | 'lastName' | 'secondLastName'> = {
  first: 'firstName',
  middle: 'middleName',
  father: 'fatherOrHusbandName',
  last: 'lastName',
  secondLast: 'secondLastName',
};

const DUPLICATE_SIGNAL: ReadonlySet<NamePart> = new Set(['first', 'middle', 'last', 'secondLast']);

function kilograms(grams: number): string {
  const locale = typeof document !== 'undefined' && document.documentElement.lang ? document.documentElement.lang : undefined;
  return new Intl.NumberFormat(locale, { style: 'unit', unit: 'kilogram', maximumFractionDigits: 2 }).format(grams / 1000);
}

export function PhoneField({
  form,
  name,
  label,
  profile,
  required,
  error,
  hint,
  className,
  onCommit,
}: {
  form: Form;
  name: 'phone' | 'altPhone' | 'guardianPhone' | 'emergencyContactPhone';
  label: string;
  profile: CountryProfile;
  required?: boolean;
  error?: string;
  hint?: string;
  className?: string;
  onCommit?: () => void;
}) {
  return (
    <Controller
      control={form.control}
      name={name}
      render={({ field }) => (
        <Field label={label} required={required} error={error} hint={hint} className={className}>
          <PhoneInput
            ref={field.ref}
            name={field.name}
            value={field.value ?? ''}
            onChange={(event) => field.onChange(event.target.value)}
            onBlur={() => {
              field.onBlur();
              onCommit?.();
            }}
            callingCode={profile.phone.callingCode}
            countryCode={profile.code}
            placeholder={profile.phone.example || undefined}
          />
        </Field>
      )}
    />
  );
}

function TextField({
  form,
  name,
  label,
  required,
  className,
  autoFocus,
  error: errorOverride,
  onCommit,
  ...input
}: {
  form: Form;
  name: FieldPath<RegisterPatientInput>;
  label: string;
  required?: boolean;
  className?: string;
  autoFocus?: boolean;
  error?: string;
  onCommit?: () => void;
  type?: string;
  dir?: string;
  lang?: string;
  autoComplete?: string;
  inputMode?: 'text' | 'email' | 'numeric';
}) {
  const registered = form.register(name);
  const raw = form.formState.errors[name as keyof RegisterPatientInput]?.message;
  const error = errorOverride ?? (typeof raw === 'string' ? raw : undefined);
  return (
    <Field label={label} required={required} error={error} className={className}>
      <Input
        {...input}
        {...registered}
        autoFocus={autoFocus}
        onBlur={(event) => {
          void registered.onBlur(event);
          onCommit?.();
        }}
      />
    </Field>
  );
}

function IdentitySection({ form, purpose, profile, facility, mode, facts, progress, autoFocusName, onLookupField, onIdentityCommit, identityExtra }: PatientFormSectionsProps) {
  const required = purpose === 'register' && identityRequired({ profile, facility }, facts);
  return (
    <RegistrationSection
      id="identity"
      title={tr('opd', 'section_identity_document')}
      description={tr('opd', 'section_identity_document_hint')}
      icon={IdCard}
      done={mode === 'full' && progress.identity}
      badge={required ? <Badge variant="outline">{tr('opd', 'required_for_adults')}</Badge> : undefined}
    >
      <IdentityDocumentFields
        form={form}
        profile={profile}
        facility={facility}
        required={required}
        autoFocus={autoFocusName}
        essentialsOnly={mode === 'quick'}
        onBlurNumber={onIdentityCommit ?? onLookupField}
      />
      {identityExtra && <div className="sm:col-span-2">{identityExtra}</div>}
    </RegistrationSection>
  );
}

function PersonalSection({ form, purpose, profile, keepsFullRecords, mode, facts, progress, autoFocusName, onLookupField, photo }: PatientFormSectionsProps) {
  const full = mode === 'full';
  const parts = nameParts(profile, mode, keepsFullRecords);
  const errors = form.formState.errors;
  const sexChoices: Choice<(typeof SEX)[number]>[] = SEX.map((sex) => ({ value: sex, label: sexLabel(sex) }));
  const bloodChoices: Choice<(typeof BLOOD_GROUP)[number]>[] = BLOOD_GROUP.map((group) => ({
    value: group,
    label: bloodGroupLabel(group),
    dir: group === 'unknown' ? undefined : 'ltr',
  }));
  const maritalChoices: Choice<(typeof MARITAL_STATUS)[number]>[] = MARITAL_STATUS.map((status) => ({ value: status, label: tr('opd', `marital_${status}`) }));

  return (
    <RegistrationSection
      id="personal"
      title={full ? tr('opd', 'section_personal_details') : tr('opd', 'section_patient')}
      description={full ? tr('opd', 'section_personal_details_hint') : tr('opd', 'section_patient_hint')}
      icon={UserRound}
      done={full && progress.personal}
      badge={facts.isMinor && !facts.infant ? <Badge variant="warning">{tr('opd', 'minor')}</Badge> : undefined}
    >
      <div className={cn('grid grid-cols-1 gap-4 sm:col-span-2', parts.length === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2')}>
        {parts.map((part, index) => (
          <TextField
            key={part}
            form={form}
            name={NAME_FIELD[part]}
            label={nameLabel(profile, part)}
            required={part === 'first'}
            autoFocus={autoFocusName && index === 0}
            autoComplete="off"
            onCommit={DUPLICATE_SIGNAL.has(part) ? onLookupField : undefined}
          />
        ))}
      </div>
      {full && profile.names.askLocalName && (
        <TextField form={form} name="fullNameLocal" label={tr('opd', 'name_in_arabic')} dir="rtl" lang="ar" className="sm:col-span-2" />
      )}
      {full && photo && <PatientPhotoField value={photo.value} onChange={photo.onChange} currentKey={photo.currentKey} name={photo.name} />}
      <Controller
        control={form.control}
        name="sex"
        render={({ field }) => (
          <Field label={tr('opd', 'sex')} required error={errors.sex?.message}>
            <ChoiceButtons value={field.value} onChange={field.onChange} onBlur={field.onBlur} options={sexChoices} required />
          </Field>
        )}
      />
      {keepsFullRecords && <DobAgeField form={form} profile={profile} required={purpose === 'register'} onDobChange={onLookupField} />}
      {!full && (
        <PhoneField form={form} name="phone" label={tr('opd', 'mobile_number')} profile={profile} error={errors.phone?.message} onCommit={onLookupField} />
      )}
      {full && keepsFullRecords && (
        <Controller
          control={form.control}
          name="bloodGroup"
          render={({ field }) => (
            <Field label={tr('opd', 'blood_group')} className="sm:col-span-2">
              <ChoiceButtons value={field.value} onChange={field.onChange} options={bloodChoices} layout="chips" />
            </Field>
          )}
        />
      )}
      {full && keepsFullRecords && (
        <Controller
          control={form.control}
          name="maritalStatus"
          render={({ field }) => (
            <Field label={tr('opd', 'marital_status')} className="sm:col-span-2">
              <ChoiceButtons value={field.value} onChange={field.onChange} options={maritalChoices} />
            </Field>
          )}
        />
      )}
    </RegistrationSection>
  );
}

function BirthSection({ form, mode, progress }: PatientFormSectionsProps) {
  const weightId = useId();
  const weeksId = useId();
  const errors = form.formState.errors;
  const grams = useWatch({ control: form.control, name: 'birthWeightGrams' });
  const weeks = useWatch({ control: form.control, name: 'gestationalAgeWeeks' });
  const weightBand = birthWeightBand(grams);
  const termBand = gestationBand(weeks);
  const weightValid = typeof grams === 'number' && grams >= BIRTH_WEIGHT_GRAMS.min && grams <= BIRTH_WEIGHT_GRAMS.max;
  const weightError = errors.birthWeightGrams ? tr('opd', 'birth_weight_range', { min: BIRTH_WEIGHT_GRAMS.min, max: BIRTH_WEIGHT_GRAMS.max }) : undefined;
  const gestationError = errors.gestationalAgeWeeks
    ? tr('opd', 'gestational_weeks_range', { min: GESTATIONAL_AGE_WEEKS.min, max: GESTATIONAL_AGE_WEEKS.max })
    : errors.gestationalAgeDays
      ? tr('opd', 'gestational_days_range', { max: GESTATIONAL_AGE_EXTRA_DAYS_MAX })
      : undefined;

  return (
    <RegistrationSection
      id="birth"
      title={tr('opd', 'section_birth_details')}
      description={tr('opd', 'section_birth_details_hint')}
      icon={Baby}
      done={mode === 'full' && progress.birth}
      badge={<Badge variant="info">{tr('opd', 'infant')}</Badge>}
    >
      <Field label={tr('opd', 'birth_weight')} htmlFor={weightId} error={weightError} hint={tr('opd', 'birth_weight_hint')}>
        <div className="flex items-center gap-2">
          <Input
            id={weightId}
            type="number"
            inputMode="numeric"
            min={BIRTH_WEIGHT_GRAMS.min}
            max={BIRTH_WEIGHT_GRAMS.max}
            step={10}
            className="w-32"
            aria-invalid={Boolean(weightError) || undefined}
            {...form.register('birthWeightGrams', { valueAsNumber: true })}
          />
          <span className="text-sm text-muted-foreground">{tr('opd', 'grams_unit')}</span>
          {weightValid && <span className="ms-auto text-xs tabular-nums text-muted-foreground">≈ {kilograms(grams)}</span>}
        </div>
      </Field>
      <Field label={tr('opd', 'gestational_age')} htmlFor={weeksId} error={gestationError} hint={tr('opd', 'gestational_age_hint')}>
        <div className="flex items-center gap-2">
          <Input
            id={weeksId}
            type="number"
            inputMode="numeric"
            min={GESTATIONAL_AGE_WEEKS.min}
            max={GESTATIONAL_AGE_WEEKS.max}
            className="w-24"
            aria-invalid={Boolean(errors.gestationalAgeWeeks) || undefined}
            {...form.register('gestationalAgeWeeks', { valueAsNumber: true })}
          />
          <span className="text-sm text-muted-foreground">{tr('opd', 'weeks_unit')}</span>
          <span className="text-sm text-muted-foreground" aria-hidden>
            +
          </span>
          <Input
            type="number"
            inputMode="numeric"
            min={0}
            max={GESTATIONAL_AGE_EXTRA_DAYS_MAX}
            className="w-20"
            aria-label={tr('opd', 'gestational_extra_days')}
            aria-invalid={Boolean(errors.gestationalAgeDays) || undefined}
            {...form.register('gestationalAgeDays', { valueAsNumber: true })}
          />
          <span className="text-sm text-muted-foreground">{tr('opd', 'days_unit')}</span>
        </div>
      </Field>
      {(weightBand || termBand) && (
        <div className="flex flex-wrap items-center gap-2 sm:col-span-2" aria-live="polite">
          {weightBand && <Badge variant={weightBand === 'normal' ? 'success' : 'warning'}>{tr('opd', `birth_weight_band_${weightBand}`)}</Badge>}
          {termBand && <Badge variant={termBand === 'term' ? 'success' : 'warning'}>{tr('opd', `gestation_band_${termBand}`)}</Badge>}
        </div>
      )}
    </RegistrationSection>
  );
}

function ContactSection({ form, profile, keepsFullRecords, mode, progress, onLookupField }: PatientFormSectionsProps) {
  const errors = form.formState.errors;
  return (
    <RegistrationSection
      id="contact"
      title={tr('opd', 'section_contact_address')}
      description={tr('opd', 'section_contact_address_hint')}
      icon={MapPin}
      done={mode === 'full' && progress.contact}
    >
      <PhoneField form={form} name="phone" label={tr('opd', 'mobile_number')} profile={profile} error={errors.phone?.message} onCommit={onLookupField} />
      {keepsFullRecords && <PhoneField form={form} name="altPhone" label={tr('opd', 'alternate_phone')} profile={profile} error={errors.altPhone?.message} />}
      <TextField form={form} name="email" label={tr('opd', 'email')} type="email" inputMode="email" autoComplete="off" />
      <TextField form={form} name="city" label={tr('opd', 'city')} />
      {keepsFullRecords && (
        <Field label={tr('opd', 'address')} className="sm:col-span-2" error={errors.address?.message}>
          <Textarea rows={2} {...form.register('address')} />
        </Field>
      )}
    </RegistrationSection>
  );
}

function GuardianSection({ form, profile, keepsFullRecords, facts, progress, family }: PatientFormSectionsProps) {
  const errors = form.formState.errors;
  const guardianPhone = useWatch({ control: form.control, name: 'guardianPhone' });
  const guardianName = useWatch({ control: form.control, name: 'guardianName' });
  const relationChoices: Choice<GuardianRelation>[] = GUARDIAN_RELATION.map((relation) => ({
    value: relation,
    label: guardianRelationLabel(relation),
    disabled: relation === 'self' && facts.isMinor,
  }));
  const linkedGuardian = Boolean(family?.patient) && family?.relation !== 'child';
  const needsGuardian = facts.showGuardianDetails && !(facts.isMinor && linkedGuardian && !guardianName?.trim());
  const title = keepsFullRecords ? tr('opd', 'section_guardian') : tr('opd', 'family');
  const description = facts.isMinor ? tr('opd', 'section_guardian_minor_hint') : tr('opd', 'section_guardian_hint');

  return (
    <RegistrationSection
      id="guardian"
      title={title}
      description={keepsFullRecords ? description : tr('opd', 'link_to_an_existing_patient_a_parent_spouse')}
      icon={HeartHandshake}
      emphasis={keepsFullRecords && facts.isMinor && !progress.guardian}
      done={keepsFullRecords && facts.showGuardianDetails && progress.guardian}
      badge={keepsFullRecords && facts.isMinor ? <Badge variant="warning">{tr('opd', 'required_under_18')}</Badge> : undefined}
    >
      {family && (
        <Field label={tr('opd', 'linked_to')} className="sm:col-span-2" hint={tr('opd', 'link_to_an_existing_patient_a_parent_spouse')} error={errors.family?.relation?.message}>
          <PatientPicker value={family.patient?.id ?? ''} onChange={(id, patient) => family.onChange(id && patient ? patient : null, family.relation)} />
        </Field>
      )}
      {family?.patient && (
        <Field label={tr('opd', 'the_linked_patient_is_this_patients')} className="sm:col-span-2">
          <Select value={family.relation} onChange={(event) => family.onChange(family.patient, event.target.value as FamilyRelation)}>
            {FAMILY_RELATION.map((relation) => (
              <option key={relation} value={relation}>
                {familyRelationLabel(relation)}
              </option>
            ))}
          </Select>
        </Field>
      )}
      {keepsFullRecords && (
        <Controller
          control={form.control}
          name="guardianRelation"
          render={({ field }) => (
            <Field label={tr('opd', 'relation_to_patient')} className="sm:col-span-2" error={errors.guardianRelation?.message ? tr('opd', 'guardian_relation_needed_for_minor') : undefined} hint={tr('opd', 'self_the_patient_manages_their_own_visits')}>
              <ChoiceButtons value={field.value} onChange={field.onChange} options={relationChoices} layout="chips" />
            </Field>
          )}
        />
      )}
      {keepsFullRecords && facts.showGuardianDetails && (
        <>
          <TextField
            form={form}
            name="guardianName"
            label={tr('opd', 'guardian_name')}
            required={needsGuardian}
            error={errors.guardianName ? tr('opd', facts.isMinor ? 'guardian_needed_for_minor' : 'guardian_name_or_self') : undefined}
          />
          <PhoneField
            form={form}
            name="guardianPhone"
            label={tr('opd', 'guardian_phone')}
            profile={profile}
            required={needsGuardian}
            error={errors.guardianPhone ? (guardianPhone?.trim() ? errors.guardianPhone.message : tr('opd', 'guardian_phone_needed')) : undefined}
          />
          {linkedGuardian && facts.isMinor && (
            <p className="flex items-start gap-2 text-xs text-muted-foreground sm:col-span-2">
              <Info className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
              {tr('opd', 'guardian_from_linked_patient')}
            </p>
          )}
        </>
      )}
    </RegistrationSection>
  );
}

function EmergencySection({ form, profile, mode, progress }: PatientFormSectionsProps) {
  const errors = form.formState.errors;
  return (
    <RegistrationSection
      id="emergency"
      title={tr('opd', 'section_emergency_contact')}
      description={tr('opd', 'section_emergency_contact_hint')}
      icon={Siren}
      done={mode === 'full' && progress.emergency}
    >
      <TextField form={form} name="emergencyContactName" label={tr('opd', 'emergency_contact_name')} />
      <PhoneField form={form} name="emergencyContactPhone" label={tr('opd', 'emergency_contact_phone')} profile={profile} error={errors.emergencyContactPhone?.message} />
    </RegistrationSection>
  );
}

function PayerSection({ form, profile, insuranceInForm }: PatientFormSectionsProps) {
  const { hasModule } = useAuth();
  const categoryChoices: Choice<(typeof PATIENT_CATEGORY)[number]>[] = PATIENT_CATEGORY.map((category) => ({ value: category, label: categoryLabel(category) }));
  const languages = languageOptions(profile);
  const languageChoices: Choice<RegisterPatientInput['preferredLanguage']>[] = languages.map((language) => ({ value: language.value, label: language.label }));
  return (
    <RegistrationSection id="payer" title={tr('opd', 'section_payer')} description={tr('opd', 'section_payer_hint')} icon={Wallet}>
      <Controller
        control={form.control}
        name="category"
        render={({ field }) => (
          <Field label={tr('opd', 'category')} hint={tr('opd', 'drives_which_tariff_prices_the_visit')} className="sm:col-span-2">
            <ChoiceButtons value={field.value} onChange={field.onChange} options={categoryChoices} layout="chips" />
          </Field>
        )}
      />
      {hasModule('insurance') && !insuranceInForm && (
        <p className="flex items-start gap-2 rounded-lg border border-dashed bg-muted/30 px-3 py-2 text-xs text-muted-foreground sm:col-span-2">
          <Info className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
          {tr('opd', 'insurance_added_on_profile')}
        </p>
      )}
      <Controller
        control={form.control}
        name="preferredLanguage"
        render={({ field }) => (
          <Field label={tr('opd', 'preferred_language')} className="sm:col-span-2">
            {languageChoices.length <= 5 ? (
              <ChoiceButtons value={field.value} onChange={field.onChange} options={languageChoices} />
            ) : (
              <Select value={field.value} onChange={(event) => field.onChange(event.target.value)}>
                {languageChoices.map((language) => (
                  <option key={language.value} value={language.value}>
                    {language.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}
      />
      <Field label={tr('opd', 'notes')} className="sm:col-span-2">
        <Textarea rows={2} {...form.register('notes')} />
      </Field>
    </RegistrationSection>
  );
}

export function PatientFormSections(props: PatientFormSectionsProps) {
  const { profile, facility, keepsFullRecords, mode, facts } = props;
  const full = mode === 'full';
  const identityFirst = identityShown({ profile, facility, mode });
  return (
    <>
      {identityFirst && <IdentitySection {...props} />}
      <PersonalSection {...props} autoFocusName={props.autoFocusName && !identityFirst} />
      {keepsFullRecords && facts.infant && <BirthSection {...props} />}
      {full && <ContactSection {...props} />}
      {(full || (keepsFullRecords && facts.showGuardianDetails)) && (keepsFullRecords || props.family) && <GuardianSection {...props} />}
      {full && keepsFullRecords && <EmergencySection {...props} />}
      {full && <PayerSection {...props} />}
      {full && props.extraSections}
    </>
  );
}

export const SECTION_ICON: Record<RegistrationSectionId, LucideIcon> = {
  identity: IdCard,
  personal: UserRound,
  birth: Baby,
  contact: MapPin,
  guardian: HeartHandshake,
  emergency: Siren,
  payer: Wallet,
};

const SECTION_TITLE_KEY: Record<RegistrationSectionId, string> = {
  identity: 'section_identity_document',
  personal: 'section_personal_details',
  birth: 'section_birth_details',
  contact: 'section_contact_address',
  guardian: 'section_guardian',
  emergency: 'section_emergency_contact',
  payer: 'section_payer',
};

export function sectionTitle(id: RegistrationSectionId, keepsFullRecords: boolean): string {
  if (id === 'guardian' && !keepsFullRecords) return tr('opd', 'family');
  return tr('opd', SECTION_TITLE_KEY[id]);
}
